import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Star, Fire, ArrowUpRight, Storefront } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
const MARKETPLACE_CATEGORIES = ["All", "LLM", "Vision", "Voice", "Image", "Video", "Speech", "Embeddings", "Inference", "Infrastructure"];
import { stripHtml } from "@/lib/stripHtml";

const TYPE_COLORS = {
  "API / Model":          "bg-blue-50 text-blue-700 border-blue-200",
  "Service":              "bg-purple-50 text-purple-700 border-purple-200",
  "API / Infrastructure": "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const CAT_ICONS = {
  LLM:           "🧠",
  Vision:        "👁️",
  Voice:         "🎙️",
  Image:         "🎨",
  Video:         "🎬",
  Speech:        "🗣️",
  Embeddings:    "🔢",
  Inference:     "⚡",
  Infrastructure:"🏗️",
};

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
      <div className="h-3 bg-slate-200 rounded w-1/4 mb-4" />
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-3 bg-slate-200 rounded w-2/3 mb-4" />
      <div className="flex gap-2 mb-4">
        {[1,2,3].map(i => <div key={i} className="h-5 bg-slate-200 rounded-full w-14" />)}
      </div>
      <div className="h-4 bg-slate-200 rounded w-1/3" />
    </div>
  );
}

function MarketplaceCard({ item }) {
  const slug = item.slug || item.id;
  const isHot = item.hot || item.is_hot;
  const isNew = item.isNew || item.is_new;
  const tags = item.tags || [];
  const rating = item.rating ?? 4.0;
  const reviews = item.reviews ?? 0;

  return (
    <Link to={`/trending-ai/marketplace/${slug}`} className={`block bg-white border rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group ${
      isHot ? "border-amber-200" : "border-slate-200"
    }`}>
      {isHot && (
        <div className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full w-fit mb-3">
          <Fire size={8} weight="fill" /> Trending
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{stripHtml(item.name)}</h3>
            {isNew && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
          </div>
          <p className="text-xs text-slate-500">{stripHtml(item.provider)}</p>
        </div>
        <div className="text-2xl shrink-0">{CAT_ICONS[item.category] || "🔧"}</div>
      </div>

      <p className="text-xs text-slate-600 leading-relaxed mb-4">{stripHtml(item.description)}</p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {item.type && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TYPE_COLORS[item.type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
            {item.type}
          </span>
        )}
        {item.category && (
          <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{item.category}</span>
        )}
        {tags.map((tag) => (
          <span key={tag} className="text-[10px] bg-slate-50 text-slate-500 px-2 py-0.5 rounded-full border border-slate-100">{tag}</span>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={s}
              size={11}
              weight={s <= Math.round(rating) ? "fill" : "regular"}
              className={s <= Math.round(rating) ? "text-amber-400" : "text-slate-200"}
            />
          ))}
        </div>
        <span className="text-xs font-semibold text-slate-700">{rating}</span>
        {reviews > 0 && <span className="text-xs text-slate-400">({reviews.toLocaleString()} reviews)</span>}
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="text-sm font-bold text-slate-800">{item.price}</div>
        <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
          Get access <ArrowUpRight size={11} />
        </span>
      </div>
    </Link>
  );
}

function CategoryBrowser({ categories, active, onSelect }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => onSelect(cat)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            active === cat ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {CAT_ICONS[cat] && <span>{CAT_ICONS[cat]}</span>}
          {cat}
        </button>
      ))}
    </div>
  );
}

export default function AIMarketplacePage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("hot");

  const fetchFn = useCallback(
    (page) => fetchAISection("marketplace", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-marketplace",
    getCacheKey: (page) => `ai-marketplace-p${page}`,
    ttlMs: 15 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((item) => {
      const matchCat = cat === "All" || item.category === cat;
      const matchSearch = !search ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        (item.provider || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.description || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      if (sort === "rating") return (b.rating ?? 0) - (a.rating ?? 0);
      if (sort === "reviews") return (b.reviews ?? 0) - (a.reviews ?? 0);
      if (sort === "name") return a.name.localeCompare(b.name);
      return ((b.hot || b.is_hot) ? 1 : 0) - ((a.hot || a.is_hot) ? 1 : 0) || ((b.isNew || b.is_new) ? 1 : 0) - ((a.isNew || a.is_new) ? 1 : 0);
    });

  const totalReviews = allItems.reduce((s, i) => s + (i.reviews ?? 0), 0);
  const avgRating = allItems.length > 0
    ? (allItems.reduce((s, i) => s + (i.rating ?? 4.0), 0) / allItems.length).toFixed(1)
    : "4.5";

  return (
    <>
      <SEO
        title="AI Marketplace — APIs, Models & Services Directory · TrendMing"
        description="Browse and compare AI APIs, models, and services: OpenAI GPT-4o, Claude API, ElevenLabs, Stable Diffusion, Whisper, Cohere, Sora. Ratings and pricing."
        canonical="https://trendming.com/trending-ai/marketplace"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Marketplace</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Marketplace</h1>
          <p className="text-slate-500 text-sm mt-1">Discover and compare AI APIs, models, and services — all in one place.</p>
        </div>

        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Storefront size={24} weight="fill" />
            <h2 className="text-base font-bold">The AI API & Service Hub</h2>
          </div>
          <p className="text-blue-100 text-sm leading-relaxed max-w-2xl mb-4">
            From foundation model APIs to voice cloning and video generation — find the right AI service for your product.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <div>
              <div className="text-2xl font-black">{allItems.length}+</div>
              <div className="text-blue-200 text-xs">services</div>
            </div>
            <div>
              <div className="text-2xl font-black">{totalReviews.toLocaleString()}</div>
              <div className="text-blue-200 text-xs">total reviews</div>
            </div>
            <div>
              <div className="text-2xl font-black">★ {avgRating}</div>
              <div className="text-blue-200 text-xs">avg. rating</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search APIs and services…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              <option value="hot">Trending</option>
              <option value="rating">Highest Rated</option>
              <option value="reviews">Most Reviewed</option>
              <option value="name">A–Z</option>
            </select>
          </div>
          <CategoryBrowser categories={MARKETPLACE_CATEGORIES} active={cat} onSelect={setCat} />
        </div>

        <div className="flex items-center gap-4 mb-5 text-xs text-slate-500">
          <span>{filtered.length} services</span>
          <span>·</span>
          <span>{filtered.filter((i) => i.hot || i.is_hot).length} trending</span>
          <span>·</span>
          <span>{filtered.filter((i) => i.isNew || i.is_new).length} new</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((item) => <MarketplaceCard key={item.id} item={item} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No services found</p>
              <p className="text-sm mt-1">Try adjusting your search or category</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load marketplace</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}

        <div className="mt-8 bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
          <h3 className="text-base font-bold text-slate-900 mb-2">Building with AI APIs?</h3>
          <p className="text-sm text-slate-500 mb-4">Use TrendMing's SEO Studio to create optimized content for your AI product.</p>
          <a href="/seo-studio" className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-xl transition-colors">
            Open SEO Studio <ArrowUpRight size={13} />
          </a>
        </div>
      </div>
    </>
  );
}
